import {
  BIRDModelController,
  PositionedModelController,
  VisualElementController,
} from '@sas-dvr/ltjs-bird';
import { ClosetObject, TypeId } from '@sas-dvr/ltjs-commons';
import { ltjsModule, typeMap } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { ActionsObservable, StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { BirdEpicDependencies } from '../epics/BirdEpic';
import { loadContainerController } from './loadContainer';
import { loadGraphElementController } from './loadGraphElement';
import { loadMediaContainerController } from './loadMediaContainer';
import { loadPositionedModelOwnerController } from './loadPositionedModelOwner';
import { loadPromptElementController } from './loadPromptElement';
import { loadReportController } from './loadReport';
import { loadSectionController } from './loadSection';
import { loadVisualController } from './loadVisual';
import { loadVisualElementController } from './loadVisualElement';
import { loadImageElementController } from './loadImage';
import { loadTextElementController } from './loadTextElement';
import { loadTableElementController } from './loadTableElement';
import { loadCrosstabElementController } from './loadCrosstabElement';
import { loadWebContentElementController } from './loadWebContentElement';
import { AnyAction } from 'redux';
import { loadKPIElementController } from './loadKPIElement';
import { loadCompositeContainerElementController } from './loadCompositeContainerElement';
import { loadVisualContainerElementController } from './loadVisualContainerElement';
import { loadVisualPromptContainerElementController } from './loadVisualPromptContainerElement';
import { loadMapElementController } from './loadMapElement';
import { loadJobContentElementController } from './loadJobContentElement';

class LoaderFactory<ControllerType extends ClosetObject, LoaderType> {
  private _loaderMap: Map<TypeId, LoaderType> = new Map();
  private _loaders: { [key: string]: LoaderType };

  constructor(loaders: { [key: string]: LoaderType }) {
    this._loaders = loaders;
  }

  private _lazyLoad() {
    for (const path in this._loaders) {
      this._addLoader(path, this._loaders[path]);
    }
  }

  private _addLoader(modulePath: string, loader: LoaderType) {
    const typeId = typeMap.typeIdReverseLookup(modulePath);
    if (typeId) {
      this._loaderMap.set(typeId, loader);
    }
  }

  private _getLoaderForTypeId(typeId: TypeId): LoaderType | undefined {
    let loader = this._loaderMap.get(typeId);
    if (!loader) {
      const typeEntry = typeMap.getTypeEntry(typeId);
      if (typeEntry) {
        loader = this._getLoaderForTypeId(typeEntry[1]);
      }
    }
    return loader;
  }

  public getLoader(controller: ControllerType): LoaderType {
    if (this._loaderMap.size === 0) {
      this._lazyLoad();
    }
    const loader = this._getLoaderForTypeId(ltjsModule.getTypeId(controller.getClosetAddress()));
    if (!loader) {
      throw new Error(`Unable to find loader for controller ${controller}`);
    }
    return loader;
  }
}

export type BIRDModelControllerLoader = (
  controller: BIRDModelController,
  unload$: Observable<BIRDModelController>,
  myActions$: ActionsObservable<AnyAction>,
  state$: StateObservable<ReportState>,
  deps: BirdEpicDependencies
) => Observable<AnyAction>;

export type VisualElementControllerLoader<T = undefined> = (
  visualController: PositionedModelController | null,
  visualElementController: VisualElementController,
  unload$: Observable<BIRDModelController>,
  myActions$: ActionsObservable<AnyAction>,
  state$: StateObservable<ReportState>,
  deps: BirdEpicDependencies,
  lifecyleActions?: { create: () => AnyAction; delete: () => AnyAction },
  extraDeps?: T
) => Observable<AnyAction>;

const birdLoaderFactory = new LoaderFactory<BIRDModelController, BIRDModelControllerLoader>({
  'sas/ltjs/BIRD/controllers/ReportController': loadReportController,
  'sas/ltjs/BIRD/controllers/view/SectionController': loadSectionController,
  'sas/ltjs/BIRD/controllers/view/PositionedModelOwnerController': loadPositionedModelOwnerController,
  'sas/ltjs/BIRD/controllers/view/VisualController': loadVisualController,
  'sas/ltjs/BIRD/controllers/view/ContainerController': loadContainerController,
  'sas/ltjs/BIRD/controllers/view/MediaContainerController': loadMediaContainerController,
});

const visualElementLoaderFactory = new LoaderFactory<
  VisualElementController,
  VisualElementControllerLoader
>({
  'sas/ltjs/BIRD/controllers/visualElements/VisualElementController': loadVisualElementController,
  'sas/ltjs/BIRD/controllers/visualElements/PromptElementController': loadPromptElementController,
  'sas/ltjs/BIRD/controllers/visualElements/RangeSliderController': loadPromptElementController,
  'sas/ltjs/BIRD/controllers/visualElements/GraphElementController': loadGraphElementController,
  'sas/ltjs/BIRD/controllers/visualElements/ImageElementController': loadImageElementController,
  'sas/ltjs/BIRD/controllers/visualElements/TextElementController': loadTextElementController,
  'sas/ltjs/BIRD/controllers/visualElements/MapElementController': loadMapElementController,
  'sas/ltjs/BIRD/controllers/visualElements/TableElementController': loadTableElementController,
  'sas/ltjs/BIRD/controllers/visualElements/CrosstabElementController': loadCrosstabElementController,
  'sas/ltjs/BIRD/controllers/visualElements/WebContentElementController': loadWebContentElementController,
  'sas/ltjs/BIRD/controllers/visualElements/JobContentElementController': loadJobContentElementController,
  'sas/ltjs/BIRD/controllers/visualElements/KPIElementController': loadKPIElementController,
  'sas/ltjs/BIRD/controllers/visualElements/VisualPromptContainerElementController': loadVisualPromptContainerElementController,
  'sas/ltjs/BIRD/controllers/visualElements/VisualContainerElementController': loadVisualContainerElementController,
  'sas/ltjs/BIRD/controllers/visualElements/CompositeContainerElementController': loadCompositeContainerElementController,
});

export function getLoaderForController(controller: BIRDModelController): BIRDModelControllerLoader {
  return birdLoaderFactory.getLoader(controller);
}

export function getLoaderForVisualElementController(
  controller: VisualElementController
): VisualElementControllerLoader {
  return visualElementLoaderFactory.getLoader(controller);
}
